import { Box, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useResolveAsset } from '../context';

type Row = Record<string, string | number>;
export type ChartKind = 'line' | 'bar' | 'area' | 'scatter';

type ChartProps = {
  kind?: ChartKind;
  /** Inline data array, OR use `src` to load a JSON asset from the CDN. */
  data?: Row[];
  /** Author-relative path to a JSON array, e.g. "data/cpi.json". */
  src?: string;
  xKey: string;
  /** One series key or several (multi-series). */
  yKeys: string | string[];
  title?: string;
  height?: number;
  /** Override series colors; defaults to theme palette. */
  colors?: string[];
};

/**
 * Recharts wrapper for MDX blogs. Authors write `<Chart kind="line"
 * data={[...]} xKey="x" yKeys={["a","b"]} />` — or point `src` at a JSON
 * asset uploaded alongside the post.
 */
const Chart: React.FC<ChartProps> = ({
  kind = 'line',
  data,
  src,
  xKey,
  yKeys,
  title,
  height = 320,
  colors,
}) => {
  const theme = useTheme();
  const resolveAsset = useResolveAsset();
  const [rows, setRows] = useState<Row[]>(data ?? []);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (data || !src) return;
    let cancelled = false;
    fetch(resolveAsset(src))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (!cancelled) setRows(json as Row[]);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [data, src, resolveAsset]);

  const series = Array.isArray(yKeys) ? yKeys : [yKeys];
  const palette = colors ?? [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];
  const colorAt = (i: number) => palette[i % palette.length];

  const axes = (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
      <XAxis dataKey={xKey} stroke={theme.palette.text.secondary} />
      <YAxis stroke={theme.palette.text.secondary} />
      <Tooltip
        contentStyle={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius,
        }}
      />
      <Legend />
    </>
  );

  let chart: React.ReactElement = <div />;
  if (kind === 'line') {
    chart = (
      <LineChart data={rows}>
        {axes}
        {series.map((k, i) => (
          <Line key={k} type="monotone" dataKey={k} stroke={colorAt(i)} strokeWidth={2} dot={false} />
        ))}
      </LineChart>
    );
  } else if (kind === 'bar') {
    chart = (
      <BarChart data={rows}>
        {axes}
        {series.map((k, i) => (
          <Bar key={k} dataKey={k} fill={colorAt(i)} />
        ))}
      </BarChart>
    );
  } else if (kind === 'area') {
    chart = (
      <AreaChart data={rows}>
        {axes}
        {series.map((k, i) => (
          <Area key={k} type="monotone" dataKey={k} stroke={colorAt(i)} fill={colorAt(i)} fillOpacity={0.3} />
        ))}
      </AreaChart>
    );
  } else if (kind === 'scatter') {
    chart = (
      <ScatterChart>
        {axes}
        {series.map((k, i) => (
          <Scatter key={k} name={k} data={rows.map((r) => ({ [xKey]: r[xKey], [k]: r[k] }))} fill={colorAt(i)} />
        ))}
      </ScatterChart>
    );
  }

  return (
    <Box sx={{ my: 4 }}>
      {title && (
        <Typography variant="h6" align="center" gutterBottom>
          {title}
        </Typography>
      )}
      {error ? (
        <Typography color="error" variant="body2">
          Failed to load chart data: {error}
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {chart}
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default Chart;
